import { Fragment, useCallback, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { useProjectBoard } from '@/features/projectBoards/hooks/useProjectBoard'
import { useCompanyMembers } from '@/features/projectBoards/hooks/useCompanyMembers'
import type { ProjectBoardColumnId } from '@/types'
import { BoardView } from './BoardView'
import { TaskDialog } from './TaskDialog'
import { withErrorCode } from '@/lib/errors'

type Props = {
  boardId: string
  parentTaskId: string
  breadcrumb: string[]
  currentUserId: string
  onClose: () => void
  onDrillIn: (childBoardId: string) => void
}

export function BoardModal({
  boardId,
  parentTaskId,
  breadcrumb,
  currentUserId,
  onClose,
  onDrillIn,
}: Props) {
  const { user } = useAuth()
  const { board, tasks, loading, error, reload } = useProjectBoard(boardId)
  const { members } = useCompanyMembers()
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [createTaskColumnId, setCreateTaskColumnId] = useState<string | null>(null)

  const selectedTask = selectedTaskId
    ? tasks.find((t) => t.id === selectedTaskId) ?? null
    : null

  const currentUser = useMemo(
    () => ({ uid: user?.uid ?? currentUserId, displayName: user?.displayName ?? null }),
    [user, currentUserId]
  )

  const nextOrderForColumn = useCallback(
    (columnId: string) => {
      const orders = tasks
        .filter((t) => t.columnId === columnId)
        .map((t) => t.order ?? 0)
      return orders.length > 0 ? Math.max(...orders) + 1 : 0
    },
    [tasks]
  )

  return (
    <>
      <motion.div
        key="backdrop"
        className="fixed inset-0 z-40 bg-black/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      <motion.div
        key="modal"
        layoutId={`task-card-${parentTaskId}`}
        className="fixed inset-4 z-50 flex flex-col overflow-hidden rounded-xl bg-background shadow-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="flex items-center justify-between gap-4 border-b px-4 py-3">
          <div className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
            {breadcrumb.map((crumb, idx) => (
              <Fragment key={idx}>
                {idx > 0 && <span className="mx-2">›</span>}
                <span
                  className={
                    idx === breadcrumb.length - 1 ? 'text-foreground font-medium' : undefined
                  }
                >
                  {crumb}
                </span>
              </Fragment>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close board"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Loading…
            </div>
          )}
          {!loading && error && (
            <div className="flex h-full items-center justify-center text-sm text-destructive">
              {error.message}
            </div>
          )}
          {!loading && !error && !board && (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {withErrorCode('Board not found.', 'project-board/not-found')}
              </div>
          )}
          {!loading && !error && board && (
            <BoardView
              boardId={boardId}
              board={board}
              tasks={tasks}
              currentUserId={currentUserId}
              onReload={reload}
              onOpenTask={(taskId) => setSelectedTaskId(taskId)}
              onCreateTaskInColumn={(columnId) => setCreateTaskColumnId(columnId)}
            />
          )}
        </div>

        {/* Edit-mode TaskDialog */}
        {board && selectedTask && (
          <TaskDialog
            open={selectedTaskId !== null}
            mode="edit"
            boardId={boardId}
            board={board}
            members={members}
            currentUser={currentUser}
            task={selectedTask}
            onClose={() => setSelectedTaskId(null)}
            onSaved={reload}
            onDrillIn={(childBoardId) => {
              setSelectedTaskId(null)
              onDrillIn(childBoardId)
            }}
          />
        )}

        {/* Create-mode TaskDialog */}
        {board && createTaskColumnId && (
          <TaskDialog
            open={createTaskColumnId !== null}
            mode="create"
            boardId={boardId}
            board={board}
            members={members}
            currentUser={currentUser}
            initialColumnId={createTaskColumnId as ProjectBoardColumnId}
            initialOrder={nextOrderForColumn(createTaskColumnId)}
            onClose={() => setCreateTaskColumnId(null)}
            onSaved={reload}
          />
        )}
      </motion.div>
    </>
  )
}

export default BoardModal
